import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import {
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
  Validators,
} from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { ForgotPasswordInterface } from '@core/models/authentication.model';
import { AuthenticationService } from '@core/services/authentication/authentication.service';
import { MessageHandlerService } from '@core/services/message-handler/message-handler.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { SESSION_KEYS } from '@feature/auth/constants/auth.constant';
import { InputModule } from '@sharedModule/components/input/input.module';
import { ATTRIBUTES_CONSTANT } from '@sharedModule/constants/fields.constant';
import { REGEX_CONSTANT } from '@sharedModule/constants/pattern-regex.constant';
import { ROUTES } from '@sharedModule/constants/route.constants';
import { showValidationMsg } from '@sharedModule/utils/form.utils';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, InputModule],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss',
})
export class ForgotPasswordComponent implements OnInit, OnDestroy {
  public readonly attributeConstant = ATTRIBUTES_CONSTANT;
  public forgotPasswordForm!: FormGroup;

  private readonly destroy$ = new Subject<void>();
  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly sessionStorageService = inject(SessionStorageService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly messageHandlerService = inject(MessageHandlerService);

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): void {
    if (!this.isFormValid()) return;

    const formData = this.forgotPasswordForm.value as ForgotPasswordInterface;
    this.sendForgotPasswordRequest(formData);
  }

  private initializeForm(): void {
    this.forgotPasswordForm = this.formBuilder.group({
      [this.attributeConstant.email.key]: [
        '',
        [Validators.required, Validators.pattern(REGEX_CONSTANT.email)],
      ],
    });
  }

  private isFormValid(): boolean {
    showValidationMsg(this.forgotPasswordForm);
    return this.forgotPasswordForm.valid;
  }

  private sendForgotPasswordRequest(formData: ForgotPasswordInterface): void {
    this.authenticationService
      .forgotPassword(formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (response.success) {
            this.storeUserEmailAndNavigate(formData.email);
          } else {
            this.showErrorMessage(response.message);
          }
        },
      });
  }

  private storeUserEmailAndNavigate(email: string): void {
    this.sessionStorageService.set(SESSION_KEYS.USER_EMAIL, email);
    this.router.navigate([ROUTES.VERIFY_FORGOT_PASSWORD_OTP]);
  }

  private showErrorMessage(message?: string): void {
    if (message) {
      this.messageHandlerService.show(message);
    }
  }
}
